import Button from "../Button/Button";

import { Navigation, Pagination, Swiper as SwiperType } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import {
	StyledCarouselCtaWrapper,
	StyledCarouselVideoWrapper,
	StyledCarouselWrapper,
} from "./Styled";

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import uuid from "react-uuid";
import "swiper/css";
import Link from "../Link/Link";

import CarouselControls from "../carouselControls/CarouselControls";
import Skeleton from "../Skeleton/Skeleton";

type Props = {
	carouselData: {
		carouselSetting: {
			showPagination: boolean;
			showNavigation: boolean;
			slidesPerView: number;
			slidesPerGroup: number;
			spaceBetween?: number;
		};
		carouselSlides: {
			type: string;
			imageAlt: string;
			slideImage: {
				desktop: {
					imageSrc: string;
				};
				mobile: {
					imageSrc: string;
				};
			};
			slideVideo: {
				desktopUrl?: string;
				mobileUrl?: string;
			};
			slideLink: {
				title: string;
				url: string;
			};
			ctaButtons: {
				buttonText: string;
				buttonLinkLocation: string;
			}[];
		}[];
	};
};

const HeroCarousel = ({ carouselData }: Props) => {
	const swiperRef = useRef<SwiperType>();
	const location = useLocation();

	const [carouselSettings, setCarouselSettings] = useState({
		showNavigation: true,
		showPagination: true,
		slidesPerView: 1,
		slidesPerGroup: 1,
	});
	const [previousSlideTitle, setPreviousSlideTitle] = useState("");
	const [nextSlideTitle, setNextSlideTitle] = useState("");

	const isLoop = (carouselData?.carouselSlides?.length || 0) >= 2;

	const populateCarouselButtons = useCallback((swiper: any) => {
		if (!swiper.slides.length) return;

		if (swiper.slides.length === 2) {
			setNextSlideTitle(
				swiper.activeIndex === 0 ? swiper.slides[1]?.dataset.slideTitle : ""
			);
			setPreviousSlideTitle(
				swiper.activeIndex === 1 ? swiper.slides[0]?.dataset.slideTitle : ""
			);
			return;
		}

		if (swiper.slides.length > 2) {
			setNextSlideTitle(
				swiper.slides.length - 1 === swiper.activeIndex
					? swiper.slides[2]?.dataset.slideTitle
					: swiper.slides[swiper.activeIndex + 1]?.dataset.slideTitle
			);

			setPreviousSlideTitle(
				swiper.activeIndex === 0
					? swiper.slides[swiper.slides.length - 3].dataset.slideTitle
					: swiper.slides[swiper.activeIndex - 1].dataset.slideTitle
			);
		}
	}, []);

	useEffect(() => {
		if (!carouselData) return;

		const {
			carouselSetting: { showNavigation, showPagination, slidesPerView, slidesPerGroup },
		} = carouselData;

		setCarouselSettings({
			showNavigation,
			showPagination,
			slidesPerView,
			slidesPerGroup,
		});
	}, [carouselData]);

	useEffect(() => {
		if (!swiperRef.current || !carouselData) return;

		const swiper = swiperRef.current;

		// resetuj na prvi slide, loop-aware
		if (isLoop) swiper.slideToLoop(0, 0);
		else swiper.slideTo(0, 0);

		populateCarouselButtons(swiper);
	}, [location.pathname, carouselData, isLoop, populateCarouselButtons]);

	const onNavigationClick = (direction: "next" | "prev") => {
		if (!swiperRef.current) return;

		direction === "next" ? swiperRef.current.slideNext() : swiperRef.current.slidePrev();
	};

	function VideoLinkTransformer<S extends string>(link: S): S {
		const videoID: S = link.split("/").slice(-1)[0] as S;
		return (`https://player.vimeo.com/video/${videoID}?h=17ec0d5818&autoplay=1&loop=1&title=0&byline=0&portrait=0&background=1` as S);
	}

	return (
		<StyledCarouselWrapper>
			{carouselData ? (
				<Swiper
					onBeforeInit={(swiper) => (swiperRef.current = swiper)}
					modules={[Pagination, Navigation]}
					pagination={{
						enabled: carouselSettings.showPagination,
						el: ".StyledPaginationWrapper",
						bulletClass: "pagination-dot",
						bulletActiveClass: "pagination-dot--active",
						clickable: true,
					}}
					navigation={{
						enabled: carouselSettings.showNavigation,
						nextEl: ".carousel-navigation--next",
						prevEl: ".carousel-navigation--prev",
					}}
					slidesPerView={carouselSettings.slidesPerView}
					slidesPerGroup={carouselSettings.slidesPerGroup}
					loop={isLoop}
					onSlideChange={(swiper) => populateCarouselButtons(swiper)}
					onSwiper={(swiper) => populateCarouselButtons(swiper)}
				>
					{carouselData.carouselSlides.map((slide) => (
						<SwiperSlide key={uuid()} data-slide-title={slide.slideLink.title}>
							<Link to={slide.slideLink.url} rel="noopener noreferrer">
								{slide.type === "Image" ? (
									<picture>
										<source
											srcSet={slide.slideImage.desktop.imageSrc}
											media="(min-width: 801px)"
										/>
										<img src={slide.slideImage.mobile.imageSrc} alt={slide.imageAlt} />
									</picture>
								) : (
									<StyledCarouselVideoWrapper
										data-vimeo-id={String(slide.slideVideo.desktopUrl)?.split("/").slice(-1)[0]}
										data-vimeo-defer
									>
										<iframe
											src={VideoLinkTransformer<string>(slide.slideVideo.desktopUrl as string)}
											loading="lazy"
											title="background video"
											allow="autoplay;"
											data-vimeo-id={String(slide.slideVideo.desktopUrl)?.split("/").slice(-1)[0]}
											data-vimeo-defer
										></iframe>
										<script src="https://player.vimeo.com/api/player.js"></script>
									</StyledCarouselVideoWrapper>
								)}
							</Link>
							{!!slide.ctaButtons.length && (
								<StyledCarouselCtaWrapper>
									{slide.ctaButtons.map((button) => (
										<Button
											variant="primary_02"
											link={{ to: button.buttonLinkLocation }}
											key={uuid()}
										>
											{button.buttonText}
										</Button>
									))}
								</StyledCarouselCtaWrapper>
							)}
						</SwiperSlide>
					))}
					<CarouselControls
						hasText
						previousText={previousSlideTitle}
						nextText={nextSlideTitle}
						onNavigationClick={onNavigationClick}
					/>
				</Swiper>
			) : (
				<Skeleton height="34.5rem" />
			)}
		</StyledCarouselWrapper>
	);
};

export default HeroCarousel;
